import { useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { HostStripeMessageSchema, stripeBridgeEvent } from "@froppio/host-auth";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { authService } from "@/core/auth/services/authService";
import { navigationState } from "@/core/routing/navigationPolicy";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useAppResume } from "@/shared/hooks/useAppResume";
import { stripeConnectionOptions, stripeKeys } from "../hooks/stripeQueries";

export function StripeHostBridge() {
  const { user } = useAuth();
  const userId = user?.id;
  const { businessId, locationId } = useParams();
  const { business } = useCurrentBusiness();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Resume signals arrive in bursts; `fetchQuery` and `cancelRefetch: false` join an in-flight refresh.
  const refresh = () => {
    if (!userId) return;
    if (authService.kind === "host" && businessId && business?.ownerId === userId) {
      // Refresh even at Home after a manual cold reopen; errors remain in the query for the settings UI.
      void queryClient.fetchQuery(stripeConnectionOptions(businessId, userId)).catch(() => {});
    } else void queryClient.invalidateQueries({ queryKey: stripeKeys.all }, { cancelRefetch: false });
  };
  const refreshRef = useRef(refresh);
  useEffect(() => { refreshRef.current = refresh; });
  useAppResume(() => refreshRef.current(), !!userId);
  useEffect(() => {
    if (!userId) return;
    const receive = (event: Event) => {
      if (authService.kind !== "host" || !(event instanceof CustomEvent)) return;
      const parsed = HostStripeMessageSchema.safeParse(event.detail);
      if (!parsed.success) return;
      if (parsed.data.type === "resume") refreshRef.current();
      if (parsed.data.type === "return" && parsed.data.result.channel === "native") {
        refreshRef.current();
        const result = parsed.data.result;
        // Never let an old browser flow switch the user's selected workspace.
        if (result.businessId === businessId && result.locationId === locationId) {
          navigate(`/b/${businessId}/l/${locationId}/stripe`, {
            state: { ...navigationState("start"), stripeOutcome: result.outcome },
          });
        }
      }
    };
    window.addEventListener(stripeBridgeEvent, receive);
    if (authService.kind === "host" && businessId && locationId) {
      window.ReactNativeWebView?.postMessage(JSON.stringify({ channel: stripeBridgeEvent, version: 1, type: "ready" }));
    }
    return () => window.removeEventListener(stripeBridgeEvent, receive);
  }, [userId, businessId, business?.ownerId, locationId, queryClient, navigate]);
  return null;
}